//is used in client
import { ValidationError } from '../../error';
import { stringValidator } from '../../validate';
import { CurrencyUnit } from '../Currency/Currency.types';
import { PayoutAccount } from './PayoutAccount.types';

export enum PayoutProviderDisplay {
  PayPal = 'PayPal',
  Bank = 'Bank',
  //add more providers for display list
}

export enum PayoutProviderCustomerType {
  DWOLLA = 'Dwolla',
  IBAN = 'IBAN',
  SORT_CODE = 'SORT_CODE',
  PayPal = 'PayPal',
}

export enum PayoutProvider {
  PayPal = 'PayPal',
  DWOLLA = 'Dwolla',
  WISE = 'WISE',
}

export type ProviderEligibilityMap = Record<PayoutProvider, boolean>;

export function isPayoutProvider(
  value: unknown,
): asserts value is PayoutProvider {
  switch (value) {
    case PayoutProvider.PayPal:
    case PayoutProvider.DWOLLA:
    case PayoutProvider.WISE:
      return;
    default:
      throw new ValidationError(value, 'payoutProvider', [
        `error validating PayoutProvider, received ${JSON.stringify(value)}`,
      ]);
  }
}

export const stringToPayoutProvider = (str: string): PayoutProvider | null => {
  try {
    stringValidator(str, 'str');
  } catch (e) {
    return null;
  }

  switch (str.toLowerCase()) {
    case 'paypal':
      return PayoutProvider.PayPal;
    case 'dwolla':
    case 'ach':
      return PayoutProvider.DWOLLA;
    case 'wise':
      return PayoutProvider.WISE;
    default:
      return null;
  }
};

export function getPayoutAccountSupportedCurrencies(
  payoutAccount: PayoutAccount,
): CurrencyUnit[] {
  switch (payoutAccount.providerName) {
    case PayoutProvider.DWOLLA:
      return [CurrencyUnit.USD];
    case PayoutProvider.PayPal:
      return [
        CurrencyUnit.USD,
        CurrencyUnit.GBP,
        CurrencyUnit.EUR,
        CurrencyUnit.CHF,
        CurrencyUnit.SEK,
      ];
    case PayoutProvider.WISE:
      if (
        payoutAccount.providerCustomerAccountID ===
        PayoutProviderCustomerType.IBAN
      ) {
        return [
          CurrencyUnit.EUR,
          CurrencyUnit.CHF,
          CurrencyUnit.SEK,
          CurrencyUnit.GBP,
        ];
      } else if (
        payoutAccount.providerCustomerAccountID ===
        PayoutProviderCustomerType.SORT_CODE
      ) {
        return [CurrencyUnit.GBP];
      } else {
        return [];
      }
    default:
      return [];
  }
}

export function getPayoutProviderCustomerTypeSupportedCurrencies(
  providerCustomerType: PayoutProviderCustomerType,
): CurrencyUnit[] {
  return {
    [PayoutProviderCustomerType.DWOLLA]: [CurrencyUnit.USD],
    [PayoutProviderCustomerType.PayPal]: [
      CurrencyUnit.USD,
      CurrencyUnit.GBP,
      CurrencyUnit.EUR,
      CurrencyUnit.CHF,
      CurrencyUnit.SEK,
    ],
    [PayoutProviderCustomerType.IBAN]: [
      CurrencyUnit.EUR,
      CurrencyUnit.CHF,
      CurrencyUnit.SEK,
      CurrencyUnit.GBP,
    ],
    [PayoutProviderCustomerType.SORT_CODE]: [CurrencyUnit.GBP],
  }[providerCustomerType];
}

export function providerToCustomerTypes(
  provider: PayoutProvider,
): PayoutProviderCustomerType[] {
  switch (provider) {
    case PayoutProvider.PayPal:
      return [PayoutProviderCustomerType.PayPal];
    case PayoutProvider.DWOLLA:
      return [PayoutProviderCustomerType.DWOLLA];
    case PayoutProvider.WISE:
      return [
        PayoutProviderCustomerType.IBAN,
        PayoutProviderCustomerType.SORT_CODE,
      ];
  }
}

export function getPayoutProviderSupportedCurrencies(
  provider: PayoutProvider,
): CurrencyUnit[] {
  const providerTypes = providerToCustomerTypes(provider);
  const currencies = providerTypes.flatMap(
    getPayoutProviderCustomerTypeSupportedCurrencies,
  );
  return [...new Set(currencies)];
}
